import React, { useCallback } from 'react';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';
import { Box, Button, Divider, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { DefaultCustomField } from 'src/constants/types';
import { MessageTemplate as MessageTemplateCard } from '../../MessageTemplate';
import { CampaignConfigType, MessageTemplate as MessageTemplateType } from '../../../types';
import { addGroupButtonSx, groupDividerSx, primaryActionButtonSx } from '../../../helpers';

interface OptOutState {
  active: boolean;
  value: string;
}

interface MessageTemplatesSectionProps {
  templates: MessageTemplateType[];
  campaignType: CampaignConfigType;
  customFields: DefaultCustomField[];
  metadataTypes: string[];
  selectedTemplateId: string | null;
  onSelectTemplate: (id: string) => void;
  onAddTemplate: (section?: 'join' | 'followup') => void;
  onUpdateTemplate: (id: string, updates: Partial<MessageTemplateType>) => void;
  onDeleteTemplate: (id: string) => void;
  onReorder: (activeId: string, overId: string) => void;
  optOut: OptOutState;
  onOptOutToggle: (active: boolean) => void;
  onOptOutValueChange: (value: string) => void;
  /** Poll configs: allow exactly one message and force its survey on (read-only). */
  isPoll?: boolean;
}

/**
 * The message-templates editor: a sortable list of message template cards
 * (reusing the existing, campaign-type-aware MessageTemplate card), an
 * add-template button, and the opt-out message toggle.
 *
 * For enrollment campaigns the list is split into Join Messages and Follow Up
 * Messages, each with its own add button; each card can be moved between
 * sections (persisted on template.messageSection).
 */
export const MessageTemplatesSection: React.FC<MessageTemplatesSectionProps> = ({
  templates,
  campaignType,
  customFields,
  metadataTypes,
  selectedTemplateId,
  onSelectTemplate,
  onAddTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
  onReorder,
  optOut,
  onOptOutToggle,
  onOptOutValueChange,
  isPoll = false,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        onReorder(String(active.id), String(over.id));
      }
    },
    [onReorder],
  );

  const renderCard = (template: MessageTemplateType, moveTo?: 'join' | 'followup') => (
    <Box key={template.id}>
      {moveTo && (
        <Box display="flex" justifyContent="flex-end" mb={0.5}>
          <Button size="small" onClick={() => onUpdateTemplate(template.id, { messageSection: moveTo })}>
            {moveTo === 'followup' ? 'Move to Follow Up' : 'Move to Join'}
          </Button>
        </Box>
      )}
      <MessageTemplateCard
        template={template}
        campaignType={campaignType}
        metadataTypes={metadataTypes}
        customFields={customFields}
        isSelected={selectedTemplateId === template.id}
        onSelect={() => onSelectTemplate(template.id)}
        onUpdate={updates => onUpdateTemplate(template.id, updates)}
        onDelete={() => onDeleteTemplate(template.id)}
        forceSurvey={isPoll}
      />
    </Box>
  );

  const optOutBlock = (
    <Box>
      <Box display="flex" alignItems="center" mb={2}>
        <FormControlLabel
          control={
            <Switch
              checked={optOut.active}
              size="small"
              onChange={e => onOptOutToggle(e.target.checked)}
              name="includeOptOut"
            />
          }
          label="Include Opt-out message"
          labelPlacement="start"
          sx={{ ml: 0, mb: -2 }}
        />
      </Box>
      {optOut.active && (
        <TextField
          fullWidth
          placeholder="Enter opt-out message..."
          value={optOut.value}
          onChange={e => onOptOutValueChange(e.target.value)}
          sx={{ mt: '10px' }}
        />
      )}
    </Box>
  );

  // Enrollment: Join + Follow Up groups with separate add buttons.
  if (campaignType === 'enrollment') {
    const joinMessages = templates.filter(t => t.messageSection !== 'followup');
    const followUpMessages = templates.filter(t => t.messageSection === 'followup');

    return (
      <Box>
        <Divider sx={groupDividerSx}>Join Messages</Divider>
        {joinMessages.map(t => renderCard(t, 'followup'))}
        <Box mt={1} mb={3} display="flex" justifyContent="flex-end">
          <Button variant="outlined" onClick={() => onAddTemplate('join')} sx={addGroupButtonSx}>
            Add Join Message
          </Button>
        </Box>

        <Divider sx={groupDividerSx}>Follow Up Messages</Divider>
        {followUpMessages.map(t => renderCard(t, 'join'))}
        <Box mt={1} mb={3} display="flex" justifyContent="flex-end">
          <Button variant="outlined" onClick={() => onAddTemplate('followup')} sx={addGroupButtonSx}>
            Add Follow Up Message
          </Button>
        </Box>

        {optOutBlock}
      </Box>
    );
  }

  if (templates.length === 0) {
    return (
      <Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height={100}
          border="2px dashed"
          borderColor="divider"
          borderRadius={2}
        >
          <Typography variant="body1" color="text.secondary">
            {`No message templates added yet. Click 'Add Message Template' to get started.`}
          </Typography>
        </Box>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button variant="contained" onClick={() => onAddTemplate()} sx={primaryActionButtonSx}>
            Add Message Template
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
      <SortableContext items={templates.map(t => t.id)} strategy={rectSortingStrategy}>
        <Box overflow="visible">
          {templates.map(t => renderCard(t))}

          {/* Poll configs allow exactly one message. */}
          {!(isPoll && templates.length >= 1) && (
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={() => onAddTemplate()} sx={primaryActionButtonSx}>
                Add Template
              </Button>
            </Box>
          )}

          {optOutBlock}
        </Box>
      </SortableContext>
    </DndContext>
  );
};
